import { useId, useState, type FormEvent } from 'react';
import {
  API_ROUTES,
  LeadAcceptedSchema,
  LeadInputSchema,
  normalizeSaudiPhone,
  type ApiError,
  type Dwelling,
  type Unit,
} from '@alrimal/contracts';
import { useLocale } from '../LocaleContext.tsx';
import { ContactActions } from './ContactActions.tsx';

/**
 * Lead capture (§7).
 *
 * Three things this does that most property forms do not:
 *
 * · The dwelling is pre-filled from the state the buyer is already looking at,
 *   so the sales call starts from the right unit.
 * · WhatsApp sits **beside** the form as a first-class action, not as a footer
 *   icon. For a large share of this audience it is the preferred channel and
 *   the form is the fallback, not the other way round.
 * · Consent is a positive act with a timestamp, and the field says what the
 *   number will be used for. An unchecked box is a validation failure, never a
 *   stored row.
 */

export interface LeadFormProps {
  units: readonly Unit[];
  selected: { unit: Unit; dwelling: Dwelling } | null;
  contactPhone: string;
  whatsappPhone: string | null;
  source: string;
}

type Status =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent'; reference: string }
  | { kind: 'failed'; message: string };

export function LeadForm({
  units,
  selected,
  contactPhone,
  whatsappPhone,
  source,
}: LeadFormProps) {
  const { t } = useLocale();
  const nameId = useId();
  const phoneId = useId();
  const dwellingId = useId();
  const messageId = useId();
  const consentId = useId();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [dwelling, setDwelling] = useState(selected?.dwelling.id ?? '');
  const [message, setMessage] = useState('');
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  const effectiveDwelling = dwelling || selected?.dwelling.id || '';

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrors({});

    const parsed = LeadInputSchema.safeParse({
      name,
      phone,
      dwellingId: effectiveDwelling === '' ? null : effectiveDwelling,
      consent: consent as true,
      ...(message.trim() === '' ? {} : { messageAr: message.trim() }),
      source,
    });

    if (!parsed.success) {
      const fields: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? 'form');
        fields[key] ??= localiseIssue(key, issue.message, t);
      }
      setErrors(fields);
      return;
    }

    setStatus({ kind: 'sending' });
    try {
      const response = await fetch(API_ROUTES.leads, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(parsed.data),
      });

      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as ApiError | null;
        setStatus({
          kind: 'failed',
          message: body?.messageAr ?? t('form.errorGeneric'),
        });
        if (body?.fields) setErrors(body.fields);
        return;
      }

      const accepted = LeadAcceptedSchema.parse(await response.json());
      setStatus({ kind: 'sent', reference: accepted.reference });
    } catch {
      setStatus({
        kind: 'failed',
        message: navigator.onLine ? t('form.errorGeneric') : t('form.offline'),
      });
    }
  }

  if (status.kind === 'sent') {
    return (
      <div className="form">
        <p className="form__status form__status--ok" role="status">
          {t('form.success')}{' '}
          <strong className="num">{status.reference}</strong>
        </p>
        <ContactActions
          phone={contactPhone}
          whatsapp={whatsappPhone}
          context={selected}
        />
      </div>
    );
  }

  return (
    <form className="form" onSubmit={onSubmit} noValidate>
      <p className="field__hint">{t('form.lede')}</p>

      <div className="field">
        <label className="field__label" htmlFor={nameId}>
          {t('form.name')}
        </label>
        <input
          id={nameId}
          className="field__control"
          name="name"
          autoComplete="name"
          placeholder={t('form.namePlaceholder')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          aria-invalid={errors['name'] !== undefined}
          aria-describedby={errors['name'] ? `${nameId}-error` : undefined}
          required
        />
        {errors['name'] ? (
          <p className="field__error" id={`${nameId}-error`}>
            {errors['name']}
          </p>
        ) : null}
      </div>

      <div className="field">
        <label className="field__label" htmlFor={phoneId}>
          {t('form.phone')}
        </label>
        <input
          id={phoneId}
          className="field__control num"
          name="phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder={t('form.phonePlaceholder')}
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          onBlur={() => {
            const normalized = normalizeSaudiPhone(phone);
            if (normalized !== null) setPhone(normalized);
          }}
          aria-invalid={errors['phone'] !== undefined}
          aria-describedby={`${phoneId}-hint${errors['phone'] ? ` ${phoneId}-error` : ''}`}
          required
        />
        <p className="field__hint" id={`${phoneId}-hint`}>
          {t('form.phoneHint')}
        </p>
        {errors['phone'] ? (
          <p className="field__error" id={`${phoneId}-error`}>
            {errors['phone']}
          </p>
        ) : null}
      </div>

      <div className="field">
        <label className="field__label" htmlFor={dwellingId}>
          {t('form.interest')}
        </label>
        <select
          id={dwellingId}
          className="field__control"
          name="dwellingId"
          value={effectiveDwelling}
          onChange={(event) => setDwelling(event.target.value)}
        >
          <option value="">—</option>
          {units.flatMap((unit) =>
            unit.dwellings.map((item) => (
              <option key={item.id} value={item.id}>
                {unit.id} · {item.labelAr}
              </option>
            )),
          )}
        </select>
      </div>

      <div className="field">
        <label className="field__label" htmlFor={messageId}>
          {t('form.message')}
        </label>
        <textarea
          id={messageId}
          className="field__control"
          name="messageAr"
          rows={2}
          maxLength={500}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
        />
      </div>

      <div className="field field--check">
        <input
          id={consentId}
          type="checkbox"
          name="consent"
          checked={consent}
          onChange={(event) => setConsent(event.target.checked)}
          aria-invalid={errors['consent'] !== undefined}
        />
        <label htmlFor={consentId}>{t('form.consent')}</label>
        {errors['consent'] ? (
          <p className="field__error" style={{ gridColumn: '1 / -1' }}>
            {errors['consent']}
          </p>
        ) : null}
      </div>

      {status.kind === 'failed' ? (
        <p className="form__status form__status--error" role="alert">
          {status.message}
        </p>
      ) : null}

      <div className="form__actions">
        <button
          type="submit"
          className="btn btn--primary"
          disabled={status.kind === 'sending'}
        >
          {status.kind === 'sending' ? t('action.sending') : t('action.send')}
        </button>
        <ContactActions
          phone={contactPhone}
          whatsapp={whatsappPhone}
          context={selected}
          compact
        />
      </div>
    </form>
  );
}

/** Zod already speaks Arabic for the custom rules; map the built-ins too. */
function localiseIssue(
  field: string,
  message: string,
  t: (key: Parameters<ReturnType<typeof useLocale>['t']>[0]) => string,
): string {
  if (field === 'consent') return t('form.consentRequired');
  if (field === 'name') return t('form.nameShort');
  if (field === 'phone') return t('form.phoneInvalid');
  return message;
}
